import type { ClimbScan, DetectedHold } from '../../../types/climb-scan';
import type { RouteSetter } from '../../../types/route-setter';

/**
 * A scanned wall a photo is the picture of, as the editor holds it once the
 * photo has been picked off the scan.
 *
 * The holds are what matter: they were measured against this very photograph,
 * so they are offered on it as placeholders, and a boulder put up on it is a
 * choice of them rather than a drawing (see `PhotoClimbEditor`).
 */
export interface WallScan {
  id: string;
  /**
   * Every hold the scan found that the wall offers, on its own 0-1000 grid —
   * less the ones the gym excluded by hand in the panel. The ones under the
   * confidence it set are here, and hidden by the filter (`minScore`).
   */
  detections: DetectedHold[];
  /** Whoever the scan says should get the next climb made off it. */
  routeSetter: RouteSetter | null;
  /**
   * The confidence the gym's staff hid the wall's holds under, where the scan
   * was read whole (the staff's reading). A member reads the wall with those
   * holds already left out, and gets null.
   */
  minScore: number | null;
}

export function wallScanFrom(scan: ClimbScan): WallScan {
  const excluded = new Set(scan.excluded_holds ?? []);

  return {
    id: scan.id,
    detections: (scan.detected_holds ?? []).filter((_hold, index) => !excluded.has(index)),
    routeSetter: pickBalancedRouteSetter(scan),
    minScore: scan.min_score ?? null,
  };
}

/**
 * Among the setters working this scan, the one with the fewest of its climbs so
 * far — so a wall reset shared between three people is split three ways without
 * anybody having to keep count.
 *
 * Carried over from the wizard this page replaced.
 */
export function pickBalancedRouteSetter(scan: ClimbScan): RouteSetter | null {
  const { route_setters: setters, colors } = scan;
  if (!setters?.length) return null;

  const counts = new Map(setters.map((setter) => [setter.id, 0]));
  for (const color of colors ?? []) {
    const id = color.climb?.route_setter?.id;
    if (id != null && counts.has(id)) counts.set(id, (counts.get(id) ?? 0) + 1);
  }

  return setters.reduce((fewest, setter) =>
    (counts.get(setter.id) ?? 0) < (counts.get(fewest.id) ?? 0) ? setter : fewest
  );
}
